function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import { useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useStateInRef, useIsMounted, FunctionalLoader } from 'seagull';
import { makeSentryIgnoredError } from "../components/ErrorBoundaryPage";
import { useIoContext, useStore } from "../contexts";
import { jsx as _jsx } from "react/jsx-runtime";
export let RestfulStrategy = /*#__PURE__*/function (RestfulStrategy) {
  RestfulStrategy["Default"] = "default";
  RestfulStrategy["PreferLoading"] = "preferLoading";
  return RestfulStrategy;
}({});

// eslint-disable-next-line @typescript-eslint/no-explicit-any

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useRestfulWrapper(options) {
  const {
    initialData,
    getStoreLocation,
    initialArgs
  } = options;
  const getStateInRef = useStateInRef(options);
  const getRefresh = useStateInRef(refresh);
  const {
    getNow
  } = useIoContext();
  const isMounted = useIsMounted();
  const storeState = useStore(state => getStoreLocation ? getStoreLocation(state) : undefined);
  const [{
    _loading,
    _error,
    _data,
    _lastSuccessfulFetch
  }, setState] = useState({
    _loading: false,
    _error: undefined,
    _data: initialData || (storeState ? storeState.data : undefined),
    _lastSuccessfulFetch: undefined
  });
  const {
    data,
    error,
    loading,
    lastSuccessfulFetch
  } = storeState || {
    data: _data,
    error: _error,
    loading: _loading,
    lastSuccessfulFetch: _lastSuccessfulFetch
  };
  useEffect(() => {
    refresh({
      args: initialArgs
    }).catch(() => {});
  }, []);
  async function refresh() {
    let options = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {};
    const {
      silent,
      args
    } = options;
    Sentry.addBreadcrumb({
      type: 'refresh',
      category: 'fetch',
      data: {
        silent
      }
    });
    try {
      !silent && setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
        _loading: true
      }));
      const res = await getStateInRef().fetch(args);
      const mounted = isMounted();
      if (mounted) {
        setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
          _error: null,
          _data: res,
          _lastSuccessfulFetch: getNow().getTime(),
          _loading: silent ? prev._loading : false
        }));
      }
      Sentry.addBreadcrumb({
        type: 'success',
        category: 'fetch',
        data: {
          mounted
        }
      });
      return res;
    } catch (err) {
      const mounted = isMounted();
      if (mounted) {
        setState(prev => _objectSpread(_objectSpread({}, prev), {}, {
          _error: err,
          _data: undefined,
          _loading: silent ? prev._loading : false
        }));
      }
      Sentry.addBreadcrumb({
        type: 'error',
        category: 'fetch',
        data: {
          mounted,
          err
        }
      });
      throw err;
    }
  }
  function renderRestfulState(props) {
    const {
      strategy = RestfulStrategy.Default,
      renderChild,
      renderError,
      renderLoader
    } = props;
    function renderErrorState() {
      if (!renderError) {
        throw makeSentryIgnoredError(error);
      }
      return renderError({
        loading,
        error,
        refresh: refresh
      });
    }
    function renderLoadingState() {
      return renderLoader ? renderLoader() : /*#__PURE__*/_jsx(FunctionalLoader, {
        show: true
      });
    }
    function renderDataState() {
      return renderChild({
        data: data,
        loading,
        refresh: refresh
      });
    }
    switch (strategy) {
      case RestfulStrategy.Default:
        return error ? renderErrorState() : data || lastSuccessfulFetch ? renderDataState() : renderLoadingState();
      case RestfulStrategy.PreferLoading:
        return error ? renderErrorState() : loading || !data ? renderLoadingState() : renderDataState();
    }
  }
  return {
    data,
    error,
    loading,
    lastSuccessfulFetch,
    refresh: options => getRefresh()(options),
    renderRestfulState
  };
}